/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * EditProfileSheet
 * ----------------
 * The one editor for the whole profile, like UpVibe's "Edit profile" dialog:
 * photo, name, gender, height, weight, mobile, age, city. A side sheet on
 * desktop, a bottom sheet on phones.
 *
 * Save order mirrors ProfileViewModel.saveProfile: the user_profile row first,
 * then the name, then — only when it changed — the mobile on the membership. A new photo is uploaded on Save (not on pick), and deleted
 * again if the row save fails so no orphan is left on the file server; the
 * replaced photo is removed only after the row points at the new one.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../../components/feed/FeedParts';
import { uploadPendingFile, deleteUploadedFile } from '../../../components/common/FileUploadInput';
import { retrieve, store } from '../../../utils/storage_helper';
import { saveUserProfile, updateName, updateMobile } from './profileApi';
import { validateProfileForm, profileChanges, membershipMobile } from './profileModel';
import RequiredDetailsFields, { requiredDetailsForm } from './RequiredDetailsFields';

const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

export default function EditProfileSheet({ api, userId, profileId, name, details, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    firstName: name.first || '',
    lastName: name.last || '',
    ...requiredDetailsForm(details),
    age: details.age === null ? '' : String(details.age),
  }));
  // undefined = unchanged, null = removed, File = new photo to upload on Save.
  const [photo, setPhoto] = useState(undefined);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const firstRef = useRef(null);
  const fileRef = useRef(null);

  const preview = useMemo(() => (photo instanceof File ? URL.createObjectURL(photo) : null), [photo]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  const shownPhoto = photo === undefined ? details.photoUrl : preview;
  const initials = ((form.firstName[0] || '') + (form.lastName[0] || '')).toUpperCase() || name.initials;

  useEffect(() => {
    firstRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { setFormError('Choose an image file (JPG, PNG or WebP).'); return; }
    if (file.size > MAX_PHOTO_BYTES) { setFormError('Choose a photo under 10 MB.'); return; }
    setFormError('');
    setPhoto(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validateProfileForm(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const changes = profileChanges(form, details);
    const first = form.firstName.trim();
    const last = form.lastName.trim();
    const nameChanged = first !== (name.first || '') || last !== (name.last || '');
    if (!Object.keys(changes).length && photo === undefined && !nameChanged) { onClose(); return; }

    setSaving(true);
    setFormError('');
    let uploadedUrl = null;
    try {
      if (photo instanceof File) {
        uploadedUrl = (await uploadPendingFile(photo, { fileType: 'image', is_public: true })).url;
        changes.profile_photo_url = uploadedUrl;
      } else if (photo === null) {
        changes.profile_photo_url = null;
      }

      if (Object.keys(changes).length) {
        try {
          await saveUserProfile(api, { userId, profileId, changes });
        } catch (err) {
          if (uploadedUrl) deleteUploadedFile(uploadedUrl);
          throw err;
        }
        if (photo !== undefined && details.photoUrl) deleteUploadedFile(details.photoUrl);
      }

      if (nameChanged) {
        await updateName(api, { firstName: first, lastName: last });
        // Keep the sidebar and top bar (which read the stored login) in step.
        const stored = retrieve('userData');
        if (stored) store('userData', { ...stored, first_name: first, last_name: last });
      }
      if ('mobile_number' in changes) {
        await updateMobile(api, membershipMobile(changes.mobile_number));
      }
      onSaved();
    } catch (err) {
      setFormError(err?.message || 'Could not save your profile. Try again.');
      setSaving(false);
    }
  };

  const errProps = (key) => ({ className: `ap-fld${errors[key] ? ' has-err' : ''}` });

  return (
    <div className="ap-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <form className="ap-sheet" role="dialog" aria-modal="true" aria-labelledby="ap-sheet-title" onSubmit={submit} noValidate>
        <span className="ap-grab" aria-hidden="true" />
        <div className="ap-sh-h">
          <h2 id="ap-sheet-title">Edit profile</h2>
          <button type="button" className="fd-iconbtn" aria-label="Close" onClick={onClose} disabled={saving}><Icon name="close" /></button>
        </div>

        <div className="ap-sh-b">
          <div className="ap-ph">
            <span className="ap-avatar">
              <span className="ap-avatar-in">{shownPhoto ? <img src={shownPhoto} alt="" /> : initials}</span>
            </span>
            <div className="ap-ph-btns">
              <button type="button" className="fd-btn fd-btn-line" onClick={() => fileRef.current?.click()} disabled={saving}>
                <Icon name="upload" />{shownPhoto ? 'Change photo' : 'Upload photo'}
              </button>
              {shownPhoto && (
                <button type="button" className="fd-btn fd-btn-line" onClick={() => setPhoto(null)} disabled={saving}>
                  <Icon name="delete" />Remove
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickPhoto} />
          </div>

          <div className="ap-fs">
            <span className="ap-eyebrow">Name</span>
            <div className="ap-f2">
              <div {...errProps('firstName')}>
                <label htmlFor="ap-first">First name</label>
                <div className="ap-inp"><input id="ap-first" ref={firstRef} value={form.firstName} onChange={set('firstName')} autoComplete="given-name" maxLength={150} /></div>
                {errors.firstName && <span className="ap-err">{errors.firstName}</span>}
              </div>
              <div className="ap-fld">
                <label htmlFor="ap-last">Last name</label>
                <div className="ap-inp"><input id="ap-last" value={form.lastName} onChange={set('lastName')} autoComplete="family-name" maxLength={150} /></div>
              </div>
            </div>
          </div>

          <div className="ap-fs">
            <span className="ap-eyebrow">About you</span>
            <RequiredDetailsFields form={form} set={set} errors={errors} />
            <div {...errProps('age')}>
              <label htmlFor="ap-age">Age</label>
              <div className="ap-inp has-u"><input id="ap-age" type="number" inputMode="numeric" min="10" max="100" value={form.age} onChange={set('age')} /><span className="ap-u">YRS</span></div>
              {errors.age && <span className="ap-err">{errors.age}</span>}
            </div>
          </div>

          {formError && <div className="ap-form-err" role="alert">{formError}</div>}
        </div>

        <div className="ap-sh-f">
          <button type="button" className="fd-btn fd-btn-line" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="fd-btn fd-btn-primary" disabled={saving}>
            <Icon name="check" />{saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
